/**
 * BibTeX and RIS → Microsoft Word's bibliography source file (Sources.xml).
 *
 * Word keeps its citation sources in Office Open XML's bibliography format (ECMA-376,
 * namespace below): a `b:Sources` root holding one `b:Source` per work, each with a
 * `b:Tag`, a `b:SourceType` and the fields that type uses. Names live two levels down,
 * `b:Author/b:Author/b:NameList/b:Person` with `b:Last`, `b:First` and `b:Middle`, or
 * `b:Corporate` for an organisation — the outer `b:Author` holds every contributor
 * role (`b:Author`, `b:Editor`, `b:Translator`…).
 *
 * The standard types every field as a string of at most 255 characters, so longer
 * values are cut and reported. `b:DOI` is not in the 2006 schema, but Word's own source
 * dialog has a DOI field and writes this element, as JabRef's exporter does.
 */

import {
  type BibEntry,
  type BibName,
  fieldText,
  isOthers,
  latexToUnicode,
  MONTH_NAMES,
  parseBibtexName,
  splitBibtexNames,
} from "@/lib/tool-kit/reference-tools/bibtex";

export const WORD_BIBLIOGRAPHY_NS =
  "http://schemas.openxmlformats.org/officeDocument/2006/bibliography";

export type WordSourceType =
  | "Book"
  | "BookSection"
  | "JournalArticle"
  | "ArticleInAPeriodical"
  | "ConferenceProceedings"
  | "Report"
  | "InternetSite"
  | "DocumentFromInternetSite"
  | "ElectronicSource"
  | "Patent"
  | "Misc";

export type WordRole =
  | "Author"
  | "BookAuthor"
  | "Editor"
  | "Translator"
  | "Inventor";

export interface WordPerson {
  last: string;
  first: string;
  middle: string;
}

/** One role's contributors: an organisation, or a list of people. */
export type WordNames =
  | { corporate: string; people?: undefined }
  | { corporate?: undefined; people: WordPerson[] };

export type WordNote =
  | "etAlDropped"
  | "truncated"
  | "noAuthor"
  | "noTitle"
  | "noYear"
  | "tagChanged";

export interface WordSource {
  tag: string;
  type: WordSourceType;
  roles: Partial<Record<WordRole, WordNames>>;
  /** Element name and text, in the order they are written. */
  fields: [string, string][];
  /** Where the record came from: its key (or position) and its original type. */
  origin: { format: "bibtex" | "ris"; label: string; type: string };
  notes: WordNote[];
}

const MAX_FIELD = 255;

/** The order fields are written in; the schema itself accepts any order. */
const FIELD_ORDER = [
  "Title",
  "ShortTitle",
  "JournalName",
  "PeriodicalTitle",
  "BookTitle",
  "ConferenceName",
  "InternetSiteTitle",
  "Year",
  "Month",
  "Day",
  "City",
  "StateProvince",
  "CountryRegion",
  "Publisher",
  "Institution",
  "Department",
  "ThesisType",
  "Edition",
  "Volume",
  "NumberVolumes",
  "Issue",
  "ChapterNumber",
  "Pages",
  "PatentNumber",
  "StandardNumber",
  "Version",
  "YearAccessed",
  "MonthAccessed",
  "DayAccessed",
  "URL",
  "DOI",
  "Comments",
];
const FIELD_RANK = new Map(FIELD_ORDER.map((name, index) => [name, index]));
const ROLE_ORDER: WordRole[] = [
  "Author",
  "BookAuthor",
  "Editor",
  "Translator",
  "Inventor",
];

const BIBTEX_TYPES: Record<string, WordSourceType> = {
  article: "JournalArticle",
  book: "Book",
  mvbook: "Book",
  booklet: "Book",
  collection: "Book",
  mvcollection: "Book",
  inbook: "BookSection",
  incollection: "BookSection",
  bookinbook: "BookSection",
  suppbook: "BookSection",
  inproceedings: "ConferenceProceedings",
  conference: "ConferenceProceedings",
  proceedings: "ConferenceProceedings",
  techreport: "Report",
  report: "Report",
  manual: "Report",
  phdthesis: "Report",
  mastersthesis: "Report",
  thesis: "Report",
  unpublished: "Report",
  online: "InternetSite",
  electronic: "InternetSite",
  www: "InternetSite",
  dataset: "ElectronicSource",
  software: "ElectronicSource",
  patent: "Patent",
};

/** The report type Word prints when the entry does not name one. */
const THESIS_TYPES: Record<string, string> = {
  phdthesis: "Doctoral dissertation",
  mastersthesis: "Master's thesis",
  thesis: "Thesis",
  techreport: "Technical report",
  unpublished: "Unpublished manuscript",
};

/** biblatex `type` codes for theses and reports, spelled out. */
const TYPE_WORDS: Record<string, string> = {
  phdthesis: "Doctoral dissertation",
  mathesis: "Master's thesis",
  candthesis: "Candidate thesis",
  techreport: "Technical report",
  resreport: "Research report",
};

const US_STATES = new Set(
  "AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY".split(
    " "
  )
);

const RE_SPACE = /\s+/;
const RE_TAG_UNSAFE = /[^A-Za-z0-9_]/g;
const RE_DIACRITICS = /\p{M}/gu;
const RE_LINK_IN_TEXT = /https?:\/\/[^\s{}]+/;
const RE_URL_WRAPPER = /^\\url\{([\s\S]*)\}$/;
const RE_URL_ESCAPE = /\\([_%#&$~])/g;
const RE_DOI_PREFIX = /^(?:https?:\/\/(?:dx\.)?doi\.org\/|doi:\s*)/i;
const RE_ISO_DATE = /^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?/;
const RE_YEAR = /\d{4}/;
const RE_NUMERIC = /^\d+$/;
const RE_PAGE_HYPHEN = /(?<=[\p{L}\p{N}])\s*-{1,2}\s*(?=[\p{L}\p{N}])/gu;
const RE_XML_SPECIAL = /[&<>"]/g;
const RE_XML_INVALID =
  /[^\t\n\r\u0020-\uD7FF\uE000-\uFFFD\u{10000}-\u{10FFFF}]/gu;
const RE_EXISTING_SOURCE = /<b:Source\b[^>]*>[\s\S]*?<\/b:Source>/g;
const RE_EXISTING_TAG = /<b:Tag>([^<]*)<\/b:Tag>/;
const RE_SOURCES_ROOT = /<b:Sources\b/;

const XML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
};

/* ------------------------------------------------------------ small parts */

function clip(value: string, notes: WordNote[]): string {
  const trimmed = value.trim();
  if (trimmed.length <= MAX_FIELD) {
    return trimmed;
  }
  if (!notes.includes("truncated")) {
    notes.push("truncated");
  }
  return `${trimmed.slice(0, MAX_FIELD - 1).trimEnd()}…`;
}

/** URLs and DOIs: unwrap `\url{}` and BibTeX escapes, but never typeset the text. */
export function plainLink(raw: string): string {
  const unwrapped = RE_URL_WRAPPER.exec(raw.trim())?.[1] ?? raw.trim();
  return unwrapped.replace(RE_URL_ESCAPE, "$1");
}

export function bareDoi(raw: string): string {
  return plainLink(raw).replace(RE_DOI_PREFIX, "");
}

function splitGiven(given: string): { first: string; middle: string } {
  const [first = "", ...rest] = given.trim().split(RE_SPACE).filter(Boolean);
  return { first, middle: rest.join(" ") };
}

/** "Chicago, IL" → City + State; "Oxford, UK" → City + Country; three parts → all three. */
function splitPlace(place: string): [string, string][] {
  const parts = place
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 3) {
    return [
      ["City", parts[0]],
      ["StateProvince", parts[1]],
      ["CountryRegion", parts[2]],
    ];
  }
  if (parts.length === 2) {
    return [
      ["City", parts[0]],
      [US_STATES.has(parts[1]) ? "StateProvince" : "CountryRegion", parts[1]],
    ];
  }
  return parts.length === 1 ? [["City", parts[0]]] : [];
}

function monthName(raw: string): string {
  const number = Number.parseInt(raw, 10);
  if (RE_NUMERIC.test(raw.trim()) && number >= 1 && number <= 12) {
    return MONTH_NAMES[number - 1];
  }
  return raw.trim();
}

/** Year, month and day from an ISO date (biblatex `date`, CSL `issued`). */
function dateFields(iso: string, prefix: "" | "Accessed"): [string, string][] {
  const match = RE_ISO_DATE.exec(iso.trim());
  if (!match) {
    return [];
  }
  const [, year, month, day] = match;
  const fields: [string, string][] = [[`Year${prefix}`, year]];
  if (month) {
    fields.push([`Month${prefix}`, monthName(month)]);
  }
  if (day) {
    fields.push([`Day${prefix}`, String(Number(day))]);
  }
  return fields;
}

/** Letters, digits and underscores, accents folded: "Müller2020" → "Muller2020". */
function tagSafe(text: string): string {
  return text
    .normalize("NFKD")
    .replace(RE_DIACRITICS, "")
    .replace(RE_TAG_UNSAFE, "");
}

/**
 * A tag Word will accept in a citation field, unique among `used` (compared without
 * case): the wanted text if it survives, else the fallback, with a, b, c… on a clash.
 */
export function wordTag(
  wanted: string,
  fallback: string,
  used: Set<string>
): string {
  const base = tagSafe(wanted) || tagSafe(fallback) || "Source";
  let tag = base;
  let counter = 0;
  while (used.has(tag.toLowerCase())) {
    counter += 1;
    tag =
      counter <= 26
        ? `${base}${String.fromCharCode(96 + counter)}`
        : `${base}${counter}`;
  }
  used.add(tag.toLowerCase());
  return tag;
}

/* ------------------------------------------------------------ from BibTeX */

function personFromBib(name: BibName): WordPerson {
  const last = [name.von, name.last, name.jr].filter(Boolean).join(" ");
  return { last, ...splitGiven(name.first) };
}

function namesFromBib(raw: string, notes: WordNote[]): WordNames | null {
  const names: BibName[] = [];
  for (const part of splitBibtexNames(raw)) {
    if (isOthers(part)) {
      if (!notes.includes("etAlDropped")) {
        notes.push("etAlDropped");
      }
      continue;
    }
    names.push(parseBibtexName(part));
  }
  if (names.length === 0) {
    return null;
  }
  if (names.length === 1 && names[0].corporate) {
    return { corporate: clip(names[0].last, notes) };
  }
  return {
    people: names.map((name) =>
      name.corporate
        ? { last: name.last, first: "", middle: "" }
        : personFromBib(name)
    ),
  };
}

interface BibReader {
  get: (name: string) => string;
  raw: (name: string) => string;
}

function bibReader(
  entry: BibEntry,
  macros: ReadonlyMap<string, string>
): BibReader {
  return {
    raw: (name) => fieldText(entry, name, macros),
    get: (name) => latexToUnicode(fieldText(entry, name, macros)),
  };
}

function bibTitle(read: BibReader): string {
  const title = read.get("title");
  const subtitle = read.get("subtitle");
  return subtitle ? `${title}: ${subtitle}` : title;
}

function containerFields(
  type: WordSourceType,
  bibType: string,
  read: BibReader
): [string, string][] {
  const journal = read.get("journal") || read.get("journaltitle");
  const booktitle = read.get("booktitle");
  switch (type) {
    case "JournalArticle":
      return [["JournalName", journal]];
    case "BookSection":
      // Classic @inbook names the book in `title` and the part in `chapter`.
      if (bibType === "inbook" && !booktitle) {
        return [["BookTitle", read.get("title")]];
      }
      return [["BookTitle", booktitle]];
    case "ConferenceProceedings":
      return [["ConferenceName", booktitle || read.get("eventtitle")]];
    case "InternetSite":
    case "DocumentFromInternetSite":
      return [
        [
          "InternetSiteTitle",
          read.get("organization") || journal || read.get("publisher"),
        ],
      ];
    default:
      return [];
  }
}

/** The title Word shows; for a classic @inbook that is the chapter, when it is named. */
function wordTitle(bibType: string, read: BibReader): string {
  const chapter = read.get("chapter");
  if (bibType === "inbook" && !read.get("booktitle") && chapter) {
    return RE_NUMERIC.test(chapter) ? read.get("title") : chapter;
  }
  return bibTitle(read);
}

function reportFields(bibType: string, read: BibReader): [string, string][] {
  const typeField = read.get("type");
  const thesisType =
    TYPE_WORDS[typeField.toLowerCase()] ??
    (typeField || THESIS_TYPES[bibType] || "");
  const issuer =
    read.get("school") || read.get("institution") || read.get("organization");
  return [
    ["ThesisType", thesisType],
    ["Publisher", read.get("publisher") || issuer],
    ["StandardNumber", read.get("number")],
  ];
}

function bibDateFields(read: BibReader): [string, string][] {
  const year = read.get("year");
  if (year) {
    return [
      ["Year", year],
      ["Month", monthName(read.get("month"))],
      ["Day", read.get("day")],
    ];
  }
  return dateFields(read.get("date"), "");
}

function typedFields(
  type: WordSourceType,
  bibType: string,
  read: BibReader
): [string, string][] {
  if (type === "Report") {
    return reportFields(bibType, read);
  }
  if (type === "Patent") {
    return [["PatentNumber", read.get("number")]];
  }
  const chapter = read.get("chapter");
  const periodical =
    type === "JournalArticle" || type === "ArticleInAPeriodical";
  // A web page's organisation is already its site title.
  const web = type === "InternetSite" || type === "DocumentFromInternetSite";
  return [
    [
      "Publisher",
      read.get("publisher") || (web ? "" : read.get("organization")),
    ],
    ["Issue", periodical ? read.get("number") || read.get("issue") : ""],
    ["ChapterNumber", RE_NUMERIC.test(chapter) ? chapter : ""],
  ];
}

/** The entry's link: `url`, or one written into `howpublished` as `\url{…}`. */
function bibUrl(read: BibReader): string {
  const url = plainLink(read.raw("url"));
  if (url) {
    return url;
  }
  return RE_LINK_IN_TEXT.exec(plainLink(read.raw("howpublished")))?.[0] ?? "";
}

function bibFields(
  type: WordSourceType,
  bibType: string,
  read: BibReader
): [string, string][] {
  const doi = read.raw("doi");
  const howpublished = read.get("howpublished");
  return [
    ["Title", wordTitle(bibType, read)],
    ["ShortTitle", read.get("shorttitle")],
    ...containerFields(type, bibType, read),
    ...bibDateFields(read),
    ...splitPlace(read.get("address") || read.get("location")),
    ...typedFields(type, bibType, read),
    ["Edition", read.get("edition")],
    ["Volume", read.get("volume")],
    ["NumberVolumes", read.get("volumes")],
    ["Pages", read.get("pages")],
    ["StandardNumber", read.get("isbn") || read.get("issn")],
    ["Version", read.get("version")],
    ...dateFields(read.raw("urldate"), "Accessed"),
    ["URL", bibUrl(read)],
    ["DOI", doi ? bareDoi(doi) : ""],
    ["Comments", read.get("note")],
    ["Comments", RE_LINK_IN_TEXT.test(howpublished) ? "" : howpublished],
  ];
}

/** The BibTeX type a record becomes in Word. */
export function wordTypeForBibtex(bibType: string, hasUrl: boolean) {
  const type = BIBTEX_TYPES[bibType.toLowerCase()];
  if (type) {
    return type;
  }
  return hasUrl ? "DocumentFromInternetSite" : "Misc";
}

/** Page ranges with the en dash Word's styles print: 263-291 → 263–291. */
export function pageRange(pages: string): string {
  return pages.replace(RE_PAGE_HYPHEN, "–");
}

function finishSource(
  draft: Omit<WordSource, "fields"> & { fields: [string, string][] }
): WordSource {
  const seen = new Set<string>();
  const fields: [string, string][] = [];
  for (const [name, value] of draft.fields) {
    if (value.trim() === "" || seen.has(name)) {
      continue;
    }
    seen.add(name);
    const text = name === "Pages" ? pageRange(value) : value;
    fields.push([name, clip(text, draft.notes)]);
  }
  fields.sort(
    (a, b) => (FIELD_RANK.get(a[0]) ?? 99) - (FIELD_RANK.get(b[0]) ?? 99)
  );
  if (!seen.has("Title")) {
    draft.notes.push("noTitle");
  }
  if (!seen.has("Year")) {
    draft.notes.push("noYear");
  }
  const contributors =
    draft.roles.Author ?? draft.roles.Editor ?? draft.roles.Inventor;
  if (!contributors) {
    draft.notes.push("noAuthor");
  }
  return { ...draft, fields };
}

function firstSurname(names: WordNames | null | undefined): string {
  if (!names) {
    return "";
  }
  return names.corporate ?? names.people?.[0]?.last ?? "";
}

function fallbackTag(names: WordNames | null | undefined, year: string) {
  return `${firstSurname(names).split(RE_SPACE).at(-1) ?? ""}${year}`;
}

export function bibEntryToWordSource(
  entry: BibEntry,
  macros: ReadonlyMap<string, string>,
  usedTags: Set<string>
): WordSource {
  const read = bibReader(entry, macros);
  const bibType = entry.type.toLowerCase();
  const type = wordTypeForBibtex(bibType, bibUrl(read) !== "");
  const notes: WordNote[] = [];
  const roles: Partial<Record<WordRole, WordNames>> = {};
  const authors = namesFromBib(read.raw("author"), notes);
  if (authors) {
    roles[type === "Patent" ? "Inventor" : "Author"] = authors;
  }
  const editors = namesFromBib(read.raw("editor"), notes);
  if (editors) {
    roles.Editor = editors;
  }
  const translators = namesFromBib(read.raw("translator"), notes);
  if (translators) {
    roles.Translator = translators;
  }
  const bookAuthors = namesFromBib(read.raw("bookauthor"), notes);
  if (bookAuthors) {
    roles.BookAuthor = bookAuthors;
  }
  const year = read.get("year") || read.get("date").slice(0, 4);
  const fallback = fallbackTag(authors ?? editors, year);
  const tag = wordTag(entry.key, fallback, usedTags);
  if (entry.key !== "" && tag !== entry.key) {
    notes.push("tagChanged");
  }
  return finishSource({
    tag,
    type,
    roles,
    fields: bibFields(type, bibType, read),
    origin: { format: "bibtex", label: entry.key, type: bibType },
    notes,
  });
}

/* --------------------------------------------------------- from CSL (RIS) */

interface CslName {
  family?: string;
  given?: string;
  suffix?: string;
  "non-dropping-particle"?: string;
  "dropping-particle"?: string;
  literal?: string;
}

const CSL_TYPES: Record<string, WordSourceType> = {
  article: "JournalArticle",
  "article-journal": "JournalArticle",
  "article-magazine": "ArticleInAPeriodical",
  "article-newspaper": "ArticleInAPeriodical",
  book: "Book",
  chapter: "BookSection",
  "entry-encyclopedia": "BookSection",
  "entry-dictionary": "BookSection",
  "paper-conference": "ConferenceProceedings",
  report: "Report",
  thesis: "Report",
  webpage: "InternetSite",
  "post-weblog": "InternetSite",
  post: "InternetSite",
  dataset: "ElectronicSource",
  software: "ElectronicSource",
  patent: "Patent",
};

const CSL_CONTAINER: Partial<Record<WordSourceType, string>> = {
  JournalArticle: "JournalName",
  ArticleInAPeriodical: "PeriodicalTitle",
  BookSection: "BookTitle",
  ConferenceProceedings: "ConferenceName",
  InternetSite: "InternetSiteTitle",
  DocumentFromInternetSite: "InternetSiteTitle",
};

function cslString(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  if (typeof value === "string") {
    return value;
  }
  return typeof value === "number" ? String(value) : "";
}

function namesFromCsl(value: unknown): WordNames | null {
  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }
  const names = value as CslName[];
  if (names.length === 1 && names[0].literal) {
    return { corporate: names[0].literal };
  }
  return {
    people: names.map((name) => {
      if (name.literal) {
        return { last: name.literal, first: "", middle: "" };
      }
      const last = [name["non-dropping-particle"], name.family, name.suffix]
        .filter(Boolean)
        .join(" ");
      return { last, ...splitGiven(name.given ?? "") };
    }),
  };
}

function cslDate(value: unknown): string {
  const parts = (value as { "date-parts"?: unknown[][] } | undefined)?.[
    "date-parts"
  ]?.[0];
  if (!Array.isArray(parts) || parts.length === 0) {
    return "";
  }
  return parts
    .map((part, index) => String(part).padStart(index === 0 ? 4 : 2, "0"))
    .join("-");
}

/**
 * A CSL record — what the site's RIS importer produces — as a Word source. Records
 * have no citation key, so the tag is built from the first surname and the year.
 */
export function cslToWordSource(
  record: Record<string, unknown>,
  position: number,
  usedTags: Set<string>
): WordSource {
  const cslType = cslString(record, "type");
  const url = cslString(record, "URL");
  const type =
    CSL_TYPES[cslType] ?? (url ? "DocumentFromInternetSite" : "Misc");
  const notes: WordNote[] = [];
  const roles: Partial<Record<WordRole, WordNames>> = {};
  const authors = namesFromCsl(record.author);
  if (authors) {
    roles[type === "Patent" ? "Inventor" : "Author"] = authors;
  }
  const editors = namesFromCsl(record.editor);
  if (editors) {
    roles.Editor = editors;
  }
  const issued = cslDate(record.issued);
  const year = RE_YEAR.exec(issued)?.[0] ?? "";
  const container = CSL_CONTAINER[type];
  const genre = cslString(record, "genre");
  const fields: [string, string][] = [
    ["Title", cslString(record, "title")],
    ...(container
      ? [[container, cslString(record, "container-title")] as [string, string]]
      : []),
    ...dateFields(issued, ""),
    ...splitPlace(
      cslString(record, "publisher-place") || cslString(record, "event-place")
    ),
    ["Publisher", cslString(record, "publisher")],
    [
      "ThesisType",
      type === "Report" ? genre || THESIS_TYPES[cslType] || "" : "",
    ],
    ["Edition", cslString(record, "edition")],
    ["Volume", cslString(record, "volume")],
    ["Issue", cslString(record, "issue")],
    ["Pages", cslString(record, "page")],
    [
      type === "Patent" ? "PatentNumber" : "StandardNumber",
      cslString(record, "number"),
    ],
    ["StandardNumber", cslString(record, "ISBN") || cslString(record, "ISSN")],
    ...dateFields(cslDate(record.accessed), "Accessed"),
    ["URL", url],
    ["DOI", bareDoi(cslString(record, "DOI"))],
  ];
  const tag = wordTag(
    fallbackTag(authors ?? editors, year),
    `Source${position + 1}`,
    usedTags
  );
  return finishSource({
    tag,
    type,
    roles,
    fields,
    origin: { format: "ris", label: String(position + 1), type: cslType },
    notes,
  });
}

/* ------------------------------------------------------------------- XML */

export function escapeXml(text: string): string {
  return text
    .replace(RE_XML_INVALID, "")
    .replace(RE_XML_SPECIAL, (char) => XML_ESCAPES[char] ?? char);
}

function element(name: string, text: string): string {
  return `<b:${name}>${escapeXml(text)}</b:${name}>`;
}

function namesXml(role: WordRole, names: WordNames, indent: string): string {
  if (names.corporate !== undefined) {
    return `${indent}<b:${role}>${element("Corporate", names.corporate)}</b:${role}>`;
  }
  const people = names.people
    .map((person) => {
      const parts = [
        element("Last", person.last),
        person.first ? element("First", person.first) : "",
        person.middle ? element("Middle", person.middle) : "",
      ].join("");
      return `${indent}    <b:Person>${parts}</b:Person>`;
    })
    .join("\n");
  return `${indent}<b:${role}>\n${indent}  <b:NameList>\n${people}\n${indent}  </b:NameList>\n${indent}</b:${role}>`;
}

export function sourceXml(source: WordSource): string {
  const lines = [
    "  <b:Source>",
    `    ${element("Tag", source.tag)}`,
    `    ${element("SourceType", source.type)}`,
  ];
  const roles = ROLE_ORDER.flatMap((role) => {
    const names = source.roles[role];
    return names ? [namesXml(role, names, "      ")] : [];
  });
  if (roles.length > 0) {
    lines.push("    <b:Author>", ...roles, "    </b:Author>");
  }
  for (const [name, value] of source.fields) {
    lines.push(`    ${element(name, value)}`);
  }
  lines.push("  </b:Source>");
  return lines.join("\n");
}

/** `b:Source` blocks from a Sources.xml Word saved, with their tags. */
export function existingSources(
  xml: string
): { blocks: string[]; tags: string[] } | null {
  if (!RE_SOURCES_ROOT.test(xml)) {
    return null;
  }
  const blocks = xml.match(RE_EXISTING_SOURCE) ?? [];
  const tags = blocks.map(
    (block) => RE_EXISTING_TAG.exec(block)?.[1]?.trim() ?? ""
  );
  return { blocks, tags };
}

/** A complete Sources.xml: any existing sources first, verbatim, then the new ones. */
export function sourcesXml(
  sources: readonly WordSource[],
  existing: readonly string[] = []
): string {
  const body = [
    ...existing.map((block) => `  ${block.trim()}`),
    ...sources.map(sourceXml),
  ].join("\n");
  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    `<b:Sources SelectedStyle="" xmlns:b="${WORD_BIBLIOGRAPHY_NS}" xmlns="${WORD_BIBLIOGRAPHY_NS}">`,
    ...(body ? [body] : []),
    "</b:Sources>",
    "",
  ].join("\n");
}
